import api from "./api";

const DEFAULT_ENDPOINT_TIMEOUT_MS = 1500;
const DEFAULT_UNSUBSCRIBE_TIMEOUT_MS = 2000;

const isSupported = () =>
  typeof window !== "undefined" &&
  "Notification" in window &&
  "serviceWorker" in navigator &&
  "PushManager" in window;

const getPermissionState = () => {
  if (!isSupported()) {
    return "unsupported";
  }

  return Notification.permission;
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

const getExistingSubscription = async () => {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  return { registration, subscription };
};

const withTimeout = (promise, timeoutMs, timeoutReason = "timeout") =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      const error = new Error(timeoutReason);
      error.code = timeoutReason;
      reject(error);
    }, timeoutMs);

    promise
      .then((result) => {
        clearTimeout(timer);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timer);
        reject(error);
      });
  });

const getCurrentEndpoint = async () => {
  if (!isSupported()) {
    return null;
  }

  const { subscription } = await getExistingSubscription();
  return subscription?.endpoint || null;
};

const getExistingSubscriptionWithTimeout = async (
  timeoutMs = DEFAULT_ENDPOINT_TIMEOUT_MS
) => {
  if (!isSupported()) {
    return {
      ok: false,
      supported: false,
      reason: "unsupported"
    };
  }

  try {
    const { registration, subscription } = await withTimeout(
      getExistingSubscription(),
      timeoutMs
    );

    return {
      ok: true,
      supported: true,
      registration,
      subscription
    };
  } catch (error) {
    const timedOut = error?.code === "timeout";
    return {
      ok: false,
      supported: true,
      reason: timedOut ? "timeout" : "subscription_lookup_failed",
      timedOut,
      error
    };
  }
};

const getCurrentEndpointSafe = async (
  timeoutMs = DEFAULT_ENDPOINT_TIMEOUT_MS
) => {
  const subscriptionState = await getExistingSubscriptionWithTimeout(timeoutMs);

  if (!subscriptionState.ok) {
    return subscriptionState;
  }

  return {
    ok: true,
    supported: true,
    endpoint: subscriptionState.subscription?.endpoint || null,
    hadSubscription: Boolean(subscriptionState.subscription)
  };
};

let subscriptionHealthPromise = null;

const persistSubscription = async (subscription) => {
  const payload = typeof subscription?.toJSON === "function"
    ? subscription.toJSON()
    : subscription;

  await api.post("/push-subscriptions", { subscription: payload });
};

const fetchVapidPublicKey = async () => {
  const { data } = await api.get("/push-subscriptions/vapid-public-key");

  if (!data?.success || !data?.publicKey) {
    throw new Error(data?.message || "Não foi possível obter a chave VAPID pública");
  }

  return data.publicKey;
};

const ensureSubscription = async ({ requestPermission = false } = {}) => {
  if (!isSupported()) {
    return {
      ok: false,
      supported: false,
      permission: "unsupported",
      reason: "unsupported"
    };
  }

  let permission = Notification.permission;

  if (permission !== "granted" && requestPermission) {
    permission = await Notification.requestPermission();
  }

  if (permission !== "granted") {
    return {
      ok: false,
      supported: true,
      permission,
      reason: "permission_not_granted"
    };
  }

  const { registration, subscription: existingSubscription } = await getExistingSubscription();
  let subscription = existingSubscription;
  let created = false;

  if (!subscription) {
    const vapidPublicKey = await fetchVapidPublicKey();
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
    created = true;
  }

  await persistSubscription(subscription);

  return {
    ok: true,
    supported: true,
    permission,
    created,
    subscription
  };
};

const ensureSubscriptionHealth = async () => {
  if (!isSupported()) {
    return {
      ok: false,
      supported: false,
      permission: "unsupported",
      reason: "unsupported"
    };
  }

  const permission = Notification.permission;
  if (permission !== "granted") {
    return {
      ok: false,
      supported: true,
      permission,
      reason: "permission_not_granted"
    };
  }

  if (subscriptionHealthPromise) {
    return subscriptionHealthPromise;
  }

  subscriptionHealthPromise = (async () => {
    const { subscription } = await getExistingSubscription();

    if (subscription) {
      await persistSubscription(subscription);
      return {
        ok: true,
        supported: true,
        permission,
        healed: false,
        subscription
      };
    }

    const ensured = await ensureSubscription({ requestPermission: false });
    return {
      ...ensured,
      healed: Boolean(ensured?.created)
    };
  })()
    .catch((error) => {
      return {
        ok: false,
        supported: true,
        permission,
        reason: "health_check_failed",
        error
      };
    })
    .finally(() => {
      subscriptionHealthPromise = null;
    });

  return subscriptionHealthPromise;
};

const ensureSubscriptionHealthSafe = async (
  timeoutMs = DEFAULT_ENDPOINT_TIMEOUT_MS
) => {
  if (!isSupported()) {
    return {
      ok: false,
      supported: false,
      permission: "unsupported",
      reason: "unsupported"
    };
  }

  const permission = Notification.permission;
  if (permission !== "granted") {
    return {
      ok: false,
      supported: true,
      permission,
      reason: "permission_not_granted"
    };
  }

  const subscriptionState = await getExistingSubscriptionWithTimeout(timeoutMs);

  if (subscriptionState.ok) {
    const { subscription } = subscriptionState;

    if (subscription) {
      try {
        await withTimeout(persistSubscription(subscription), timeoutMs);
        return {
          ok: true,
          supported: true,
          permission,
          healed: false,
          subscription
        };
      } catch (error) {
        return {
          ok: false,
          supported: true,
          permission,
          reason: error?.code === "timeout" ? "timeout" : "health_check_failed",
          timedOut: error?.code === "timeout",
          error
        };
      }
    }
  } else if (subscriptionState.reason === "timeout") {
    return {
      ok: false,
      supported: true,
      permission,
      reason: "timeout",
      timedOut: true
    };
  }

  try {
    const ensured = await withTimeout(
      ensureSubscription({ requestPermission: false }),
      timeoutMs
    );
    return {
      ...ensured,
      healed: Boolean(ensured?.created)
    };
  } catch (error) {
    return {
      ok: false,
      supported: true,
      permission,
      reason: error?.code === "timeout" ? "timeout" : "health_check_failed",
      timedOut: error?.code === "timeout",
      error
    };
  }
};

const unsubscribeByEndpoint = async ({ notifyBackend = true } = {}) => {
  if (!isSupported()) {
    return {
      ok: false,
      supported: false,
      reason: "unsupported"
    };
  }

  const { subscription } = await getExistingSubscription();

  if (!subscription) {
    return {
      ok: true,
      supported: true,
      hadSubscription: false
    };
  }

  const endpoint = subscription.endpoint;
  let backendOk = true;
  let backendError = null;

  if (notifyBackend) {
    try {
      await api.delete("/push-subscriptions/by-endpoint", {
        data: { endpoint }
      });
    } catch (error) {
      backendOk = false;
      backendError = error;
    }
  }

  let localUnsubscribed = true;
  let localError = null;
  try {
    localUnsubscribed = (await subscription.unsubscribe()) !== false;
  } catch (error) {
    localUnsubscribed = false;
    localError = error;
  }

  return {
    ok: backendOk && localUnsubscribed,
    supported: true,
    hadSubscription: true,
    backendOk,
    backendError,
    localUnsubscribed,
    localError,
    endpoint
  };
};

const unsubscribeByEndpointSafe = async (
  { notifyBackend = true } = {},
  timeoutMs = DEFAULT_UNSUBSCRIBE_TIMEOUT_MS
) => {
  const subscriptionState = await getExistingSubscriptionWithTimeout(timeoutMs);

  if (!subscriptionState.ok) {
    return subscriptionState;
  }

  const { subscription } = subscriptionState;

  if (!subscription) {
    return {
      ok: true,
      supported: true,
      hadSubscription: false
    };
  }

  const endpoint = subscription.endpoint;
  let backendOk = true;
  let backendError = null;

  if (notifyBackend) {
    try {
      await withTimeout(
        api.delete("/push-subscriptions/by-endpoint", {
          data: { endpoint }
        }),
        timeoutMs
      );
    } catch (error) {
      backendOk = false;
      backendError = error;
    }
  }

  let localUnsubscribed = true;
  let localError = null;

  try {
    localUnsubscribed = await withTimeout(
      Promise.resolve(subscription.unsubscribe()).then((result) => result !== false),
      timeoutMs
    );
  } catch (error) {
    localUnsubscribed = false;
    localError = error;
  }

  return {
    ok: backendOk && localUnsubscribed,
    supported: true,
    hadSubscription: true,
    backendOk,
    backendError,
    localUnsubscribed,
    localError,
    endpoint
  };
};

const pushNotifications = {
  isSupported,
  getPermissionState,
  ensureSubscription,
  ensureSubscriptionHealth,
  ensureSubscriptionHealthSafe,
  getCurrentEndpoint,
  getCurrentEndpointSafe,
  unsubscribeByEndpoint,
  unsubscribeByEndpointSafe,
  urlBase64ToUint8Array
};

export default pushNotifications;
